import { Link } from 'react-router-dom';
import SvgAsset from '../../SvgAsset';
import { FeedbackMessage } from '../FeedbackMessage';
import {
    ConfirmPasswordField,
    EmailField,
    NameFields,
    PasswordRequirementsField,
    SubmitButton,
} from '../fields';
import type { AuthTemplateProps } from '../types';

export default function SignupTemplate(props: AuthTemplateProps) {
    const hasOauthProviders = props.oauthProviders.length > 0 && !props.signupDisabled;

    return (
        <section className="mx-auto max-w-lg space-y-4">
            <div className="p-8 bg-coconut rounded-lg space-y-5">
                <h1 className="text-2xl font-semibold">Create your account</h1>
                <p className="text-base text-licorice">
                    Create an Insureworks account to continue to {props.destination}.
                </p>

                <FeedbackMessage feedback={props.message} />
                <FeedbackMessage feedback={props.error} tone="error" />

                <form className="space-y-4" onSubmit={props.onSubmit} noValidate>
                    {hasOauthProviders && (
                        <>
                            <div className="space-y-2">
                                {props.oauthProviders.map((provider) => (
                                    <a
                                        key={provider.id}
                                        className="btn btn-sm btn-outline-licorice w-full"
                                        href={props.oauthStartHref(provider.id, 'signup')}
                                    >
                                        {provider.logo && (
                                            <SvgAsset
                                                className="w-5 h-auto mr-3.5"
                                                src={provider.logo}
                                            />
                                        )}
                                        Sign up with {provider.label}
                                    </a>
                                ))}
                            </div>

                            <div className="flex items-center gap-3.5 my-6 -mx-8 text-xs font-bold uppercase tracking-wide text-oyster">
                                <span className="flex-1 border-b-2 border-marshmallow" />
                                <span className="flex-none">or</span>
                                <span className="flex-1 border-b-2 border-marshmallow" />
                            </div>
                        </>
                    )}

                    {!props.signupDisabled && <NameFields {...props} />}
                    <EmailField {...props} />
                    <PasswordRequirementsField
                        {...props}
                        requirements={props.requirements}
                        label="Password"
                    />
                    <ConfirmPasswordField {...props} />

                    {props.signupDisabled ? (
                        <FeedbackMessage
                            feedback={{
                                message:
                                    'Signup is not available for this app. Log in with an existing staff account.',
                                type: 'info',
                            }}
                        />
                    ) : (
                        <SubmitButton label="Register" loading={props.loading} />
                    )}
                </form>
            </div>

            <div className="mt-4 text-sm text-licorice">
                <span className="inline-block mr-0.5">Already have an account?</span>
                <Link
                    className="text-sm font-semibold underline decoration-dotted hover:no-underline"
                    to={props.authHref('login')}
                >
                    Log in
                </Link>
            </div>
        </section>
    );
}
